import { useEffect, useState } from 'react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend } from 'recharts';
import { Download } from 'lucide-react';
import api from '@/lib/api';
import { exportCsv } from '@/lib/csv';
import { useSort, SortTh, TableHeadRow } from '@/components/ui/sortable-table';

interface TeamMember {
  userId: string;
  name: string;
  role: string;
  totalLeads: number;
  convertedLeads: number;
  conversionRate: number;
  totalTasks: number;
  completedTasks: number;
}

interface Props {
  dateFrom: string;
  dateTo: string;
}

export function TeamPerformance({ dateFrom, dateTo }: Props) {
  const [data, setData] = useState<TeamMember[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    api
      .get('/reports/team', { params: { dateFrom, dateTo } })
      .then(r => setData(r.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [dateFrom, dateTo]);

  const { sorted, sort, onSort } = useSort(data);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="h-6 w-6 animate-spin rounded-full border-2 border-[#6366F1] border-t-transparent" />
      </div>
    );
  }

  if (data.length === 0) {
    return <p className="text-sm text-slate-400 py-12 text-center">No team data available</p>;
  }

  const chartData = data
    .filter(d => d.totalLeads > 0 || d.totalTasks > 0)
    .slice(0, 10);

  const handleExport = () => {
    exportCsv('team_performance', data, [
      { key: 'name', label: 'Name' },
      { key: 'role', label: 'Role' },
      { key: 'totalLeads', label: 'Total Leads' },
      { key: 'convertedLeads', label: 'Converted' },
      { key: 'conversionRate', label: 'Conversion %' },
      { key: 'totalTasks', label: 'Total Tasks' },
      { key: 'completedTasks', label: 'Tasks Done' },
    ]);
  };

  return (
    <div className="space-y-5">
      <div className="flex justify-end">
        <button
          onClick={handleExport}
          className="inline-flex items-center gap-2 px-3 py-1.5 text-xs font-semibold rounded-lg border border-slate-200 bg-white text-slate-700 hover:border-[#6366F1] hover:text-[#6366F1] transition-colors"
        >
          <Download className="h-3.5 w-3.5" />
          Export CSV
        </button>
      </div>
      {/* Chart */}
      {chartData.length > 0 && (
        <div className="bg-white rounded-xl p-5 shadow-sm">
          <h3 className="text-sm font-semibold text-[#1E293B] mb-4">Team Comparison</h3>
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={chartData}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
              <XAxis dataKey="name" tick={{ fontSize: 11 }} />
              <YAxis tick={{ fontSize: 11 }} allowDecimals={false} />
              <Tooltip />
              <Legend />
              <Bar dataKey="totalLeads" fill="#6366F1" radius={[4, 4, 0, 0]} name="Total Leads" />
              <Bar dataKey="convertedLeads" fill="#10B981" radius={[4, 4, 0, 0]} name="Converted" />
              <Bar dataKey="completedTasks" fill="#F59E0B" radius={[4, 4, 0, 0]} name="Tasks Done" />
            </BarChart>
          </ResponsiveContainer>
        </div>
      )}

      {/* Table */}
      <div className="bg-white rounded-xl shadow-sm overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <TableHeadRow>
                <SortTh label="Name" sortKey="name" sort={sort} onSort={onSort} />
                <SortTh label="Role" sortKey="role" sort={sort} onSort={onSort} />
                <SortTh label="Leads" sortKey="totalLeads" sort={sort} onSort={onSort} align="right" />
                <SortTh label="Converted" sortKey="convertedLeads" sort={sort} onSort={onSort} align="right" />
                <SortTh label="Conv. Rate" sortKey="conversionRate" sort={sort} onSort={onSort} align="right" />
                <SortTh label="Tasks" sortKey="totalTasks" sort={sort} onSort={onSort} align="right" />
                <SortTh label="Completed" sortKey="completedTasks" sort={sort} onSort={onSort} align="right" />
              </TableHeadRow>
            </thead>
            <tbody>
              {sorted.map(m => (
                <tr key={m.userId} className="border-b border-gray-50 hover:bg-gray-50/50">
                  <td className="px-4 py-3 font-medium text-slate-800">{m.name}</td>
                  <td className="px-4 py-3 text-slate-600 capitalize">{m.role}</td>
                  <td className="px-4 py-3 text-right text-slate-700">{m.totalLeads}</td>
                  <td className="px-4 py-3 text-right text-emerald-600 font-medium">{m.convertedLeads}</td>
                  <td className="px-4 py-3 text-right">
                    <span className={`font-medium ${m.conversionRate >= 50 ? 'text-emerald-600' : m.conversionRate >= 20 ? 'text-amber-600' : 'text-slate-500'}`}>
                      {m.conversionRate}%
                    </span>
                  </td>
                  <td className="px-4 py-3 text-right text-slate-700">{m.totalTasks}</td>
                  <td className="px-4 py-3 text-right text-[#6366F1] font-medium">{m.completedTasks}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
